import { strapiClient } from "@shared/api";
import rebuild from "@shared/api/rebuild";
import { NotFoundError } from "@shared/utils/not-found-error";
import { getSeoConfig } from "@shared/utils/seo/seo-helper";

import { type CulturalObject, mapDtoToCulturalObjectPoint } from "../model";
import { searchParams } from "./params";

type CulturalObjectPageResponse = {
  culturalObject: CulturalObject;
};

export const getCulturalObjectPage = async (
  slug: string,
): Promise<CulturalObjectPageResponse> => {
  const pageSearchParams = {
    filters: {
      uid: {
        $eq: slug,
      },
    },
    populate: {
      backgroundImage: {
        populate: "*",
      },
      workTimeList: {
        populate: "*",
      },
      content: {
        on: {
          "article.section-name": {
            populate: "*",
          },
          "article.text-block": {
            populate: "*",
          },
          "article.quote-with-author": {
            populate: "*",
          },
          "article.table": {
            populate: {
              tabletRows: {
                populate: "*",
              },
              columnTitle: {
                populate: "*",
              },
              columnSubtitle: {
                populate: "*",
              },
            },
          },
          "article.gallery": {
            populate: "*",
          },
          "article.video": {
            populate: "*",
          },
        },
      },
      banner: {
        populate: {
          photo: {
            populate: "*",
          },
          icon: {
            populate: "*",
          },
        },
      },
      gallerySection: {
        populate: {
          gallery: {
            populate: {
              backgroundMedia: {
                populate: {
                  image: {
                    populate: "*",
                  },
                  video: {
                    populate: "*",
                  },
                },
              },
            },
          },
        },
      },
      cultural_object_type: {
        populate: "*",
      },
      seo: {
        populate: {
          ogImage: {
            populate: "*",
          },
        },
      },
      other_objects: {
        populate: searchParams.singleObject.populate,
      },
    },
  };

  const culturalObjects = strapiClient.collection("cultural-objects");

  const page = await culturalObjects.find({ ...pageSearchParams });

  const culturalObjectsPoints = mapDtoToCulturalObjectPoint(page);

  const currentCulturalObject = culturalObjectsPoints[0];

  if (!currentCulturalObject) {
    throw new NotFoundError(`CulturalObject with slug "${slug}" not found`);
  }

  return rebuild({
    culturalObject: {
      ...currentCulturalObject,
      seo: getSeoConfig.culturalObject(currentCulturalObject),
    },
  }) as CulturalObjectPageResponse;
};
